import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../lib/api';

/**
 * Branding context — fetches app name / logo / favicon / tab title from the backend
 * and applies them globally. GET /api/settings/branding is public so this works
 * before login too.
 *
 * Multi-tenancy ready: when the logged-in user has an organization_id, we
 * re-fetch with ?organization_id=... to get that tenant's branding.
 */
const DEFAULTS = {
  app_name: 'Mieteora',
  app_subtitle: 'Property Management',
  browser_title: 'Mieteora',
  logo_data_uri: '',
  favicon_data_uri: '/icon.svg',
  primary_color: '',
  document_logo_data_uri: '',
  document_logo_height_mm: 22,
};

const BrandingContext = createContext({ branding: DEFAULTS, reload: () => {}, loading: true });

export const useBranding = () => useContext(BrandingContext);

export const BrandingProvider = ({ children, organizationId = null }) => {
  const [branding, setBranding] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      let effectiveOrganizationId = organizationId;
      if (!effectiveOrganizationId) {
        effectiveOrganizationId = localStorage.getItem('activeOrganizationId');
      }
      if (!effectiveOrganizationId) {
        try {
          const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
          effectiveOrganizationId = storedUser.organization_id || storedUser.organizationId || null;
        } catch {
          effectiveOrganizationId = null;
        }
      }

      const params = effectiveOrganizationId ? { organization_id: effectiveOrganizationId } : {};
      const res = await api.get('/settings/branding', { params });
      if (res.data) setBranding({ ...DEFAULTS, ...res.data });
    } catch (e) {
      console.warn('Branding fetch failed — using defaults:', e?.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { reload(); }, [reload]);

  // Apply <title> + favicon whenever branding changes.
  useEffect(() => {
    const title = branding.browser_title || branding.app_name || DEFAULTS.browser_title;
    if (title) document.title = title;

    const favicon = branding.favicon_data_uri || DEFAULTS.favicon_data_uri;
    if (favicon) {
      let link = document.querySelector('link[rel="icon"]');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = favicon;
      if (favicon.endsWith('.svg')) {
        link.type = 'image/svg+xml';
      }
    }
  }, [branding.browser_title, branding.app_name, branding.favicon_data_uri]);

  return (
    <BrandingContext.Provider value={{ branding, reload, loading }}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingProvider;
